import React, {useState} from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    Bolt,
    CalendarMonth,
    LocationOn,
    Public,
    Search as SearchIcon
} from '@mui/icons-material';
import {Head, Link, router, usePage} from '@inertiajs/react';
import {Competition} from '@/models/Competition';
import {brand} from '@/Utils/Theme';
import MainLayout from '@/Layouts/MainLayout';

interface CompetitionsData {
    inscription_closing_soon: Competition[];
    inscription_open: Competition[];
    upcoming: Competition[];
    past: Competition[];
    stats: {
        total_competitions: number;
        active_inscriptions: number;
        upcoming_competitions: number;
        past_competitions: number;
    };
}

interface HomeProps {
    competitions: CompetitionsData;
    search?: string;
}

const getAllCompetitions = (competitions: CompetitionsData) => [
    ...competitions.inscription_closing_soon,
    ...competitions.inscription_open,
    ...competitions.upcoming,
    ...competitions.past,
];

const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Fecha por confirmar';

    return new Date(dateString).toLocaleDateString('es-AR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

const getCompetitionImage = (competition: Competition) => {
    const image = competition.banner_url || competition.logo_url;

    if (!image) return undefined;

    return image.includes('http') ? image : `/storage/${image}`;
};

const getCompetitionPlace = (competition: Competition) => {
    if (competition.type === 'online') return 'Online';

    return competition.address || 'Presencial';
};

const isCompetitionFinished = (competition: Competition) => {
    const reference = competition.end_date || competition.start_date;

    if (!reference) return false;

    return new Date(reference) < new Date();
};

const MiniCompetitionCard = ({competition}: { competition: Competition }) => (
    <Card
        component={Link}
        href={`/competitions/${competition.slug}`}
        sx={{
            display: 'block',
            height: '100%',
            color: 'inherit',
            textDecoration: 'none',
            borderRadius: 5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
            '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
                borderColor: brand.amarillo,
            },
        }}
    >
        {getCompetitionImage(competition) ? (
            <CardMedia component="img" image={getCompetitionImage(competition)} alt={competition.name} sx={{height: 164, objectFit: 'cover'}} />
        ) : (
            <Box sx={{height: 164, background: `linear-gradient(135deg, ${brand.intermedio} 0%, ${brand.intenso} 100%)`}} />
        )}
        <CardContent>
            <Stack direction="row" spacing={1} sx={{mb: 1}}>
                <Chip size="small" label={competition.type === 'online' ? 'Online' : 'Presencial'} color="primary" />
                {isCompetitionFinished(competition) ? (
                    <Chip
                        size="small"
                        label="Finalizada"
                        variant="outlined"
                        sx={{color: brand.suave, borderColor: brand.intermedio}}
                    />
                ) : (
                    competition.inscription_end_date && <Chip size="small" label="Inscripción" variant="outlined" />
                )}
            </Stack>
            <Typography variant="h6" sx={{fontWeight: 850, lineHeight: 1.15, color: 'text.primary'}}>
                {competition.name}
            </Typography>
            <Stack spacing={0.75} sx={{mt: 1.5}}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarMonth sx={{fontSize: 18, color: 'text.secondary'}} />
                    <Typography variant="body2" sx={{color: 'text.secondary'}}>
                        {formatDate(competition.start_date)}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOn sx={{fontSize: 18, color: 'text.secondary'}} />
                    <Typography variant="body2" noWrap sx={{color: 'text.secondary'}}>
                        {getCompetitionPlace(competition)}
                    </Typography>
                </Stack>
            </Stack>
        </CardContent>
    </Card>
);

const EmptyState = ({search}: { search?: string }) => (
    <Box
        sx={{
            p: 4,
            borderRadius: 5,
            border: '1px dashed',
            borderColor: 'divider',
            color: 'text.secondary',
            textAlign: 'center',
        }}
    >
        <Typography variant="h6" sx={{fontWeight: 800, color: 'text.primary'}}>
            No hay competencias publicadas todavía.
        </Typography>
        <Typography sx={{mt: 1}}>
            {search
                ? `No encontramos eventos que coincidan con "${search}". Probá con otro nombre.`
                : 'Este bloque queda listo para llenarse con eventos reales.'}
        </Typography>
    </Box>
);

const Home: React.FC = () => {
    const {competitions, search} = usePage<HomeProps>().props;
    const [term, setTerm] = useState(search ?? '');

    const submitSearch = (event: React.FormEvent) => {
        event.preventDefault();
        router.get('/', {search: term}, {preserveState: true, preserveScroll: true});
    };

    const results = search ? getAllCompetitions(competitions) : getAllCompetitions(competitions).slice(0, 6);

    return (
        <MainLayout>
            <Head title="YoCompito - Plataforma de Competencias CrossFit" />

            {/* Hero */}
            <Box sx={{maxWidth: 980, mx: 'auto', textAlign: 'center', pt: {xs: 1, md: 3}}}>
                <Chip label="Event Marketplace" color="primary" sx={{mb: 3, fontWeight: 800}} />
                <Typography variant="h1" sx={{fontSize: {xs: 40, md: 68}, lineHeight: 0.98, fontWeight: 950, letterSpacing: '-0.06em', color: 'text.primary'}}>
                    Descubrí competencias como elegís tu próximo destino.
                </Typography>
                <Typography variant="h6" sx={{mt: 3, color: 'text.secondary', lineHeight: 1.55}}>
                    Una vidriera limpia para encontrar eventos por fecha, modalidad y estado de inscripción, con el camino directo al registro.
                </Typography>
            </Box>

            {/* Google-style search box (submits ?search= to the server) */}
            <Box component="form" onSubmit={submitSearch} sx={{mt: 5, mx: 'auto', maxWidth: 720, px: 1}}>
                <TextField
                    fullWidth
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    placeholder="Buscá tu competencia por nombre…"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{color: brand.intermedio}} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    type="submit"
                                    aria-label="Buscar"
                                    sx={{
                                        bgcolor: brand.amarillo,
                                        color: brand.intenso,
                                        '&:hover': {bgcolor: '#d9eb1f'},
                                    }}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: 999,
                            bgcolor: brand.base,
                            color: brand.intenso,
                            py: 0.5,
                            pl: 2,
                            pr: 0.75,
                            boxShadow: '0 20px 70px rgba(0,0,0,0.45)',
                            '& input::placeholder': {color: brand.suave, opacity: 1},
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {border: 'none'},
                    }}
                />
            </Box>

            <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1.5} sx={{mt: 4}}>
                <Chip icon={<Bolt />} label={`${competitions.stats.active_inscriptions} abiertas`} color="primary" sx={{fontWeight: 700}} />
                <Chip icon={<CalendarMonth />} label={`${competitions.stats.upcoming_competitions} próximas`} variant="outlined" sx={{color: 'text.primary', borderColor: 'divider'}} />
                <Chip icon={<Public />} label="Online y presencial" variant="outlined" sx={{color: 'text.primary', borderColor: 'divider'}} />
            </Stack>

            <Typography variant="h4" sx={{mt: 7, mb: 3, fontWeight: 950, color: 'text.primary'}}>
                {search ? `Resultados para "${search}"` : 'Eventos destacados'}
            </Typography>
            {results.length > 0 ? (
                <Grid container spacing={3}>
                    {results.map((competition) => (
                        <Grid item xs={12} sm={6} lg={4} key={competition.id}>
                            <MiniCompetitionCard competition={competition} />
                        </Grid>
                    ))}
                </Grid>
            ) : <EmptyState search={search} />}
        </MainLayout>
    );
};

export default Home;
